import React, { useState } from 'react';
import { Heart, Users, Shield, Lock } from 'lucide-react';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';

const HCS_LOGO_URL = 'https://customer-assets.emergentagent.com/job_203c254d-590f-4582-b077-890a39b58283/artifacts/vvnnctky_HCS%20Logo.jpg';

const Login = ({ onLogin }) => {
  const { t } = useLanguage();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [states, setStates] = useState([]);
  const [accessConfig, setAccessConfig] = useState({ access_protection_enabled: false, invite_code_required: false });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'caregiver',
    phone: '',
    language: 'en',
    state: '',
    district: '',
    access_code: '',
    invite_code: ''
  });

  // Fetch available states and access config on mount
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [statesRes, configRes] = await Promise.all([
          api.getAvailableStates(),
          api.getAccessConfig()
        ]);
        setStates(statesRes.data.states || []);
        setAccessConfig(configRes.data || { access_protection_enabled: false, invite_code_required: false });
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        // Include invite_code if required
        const registerData = { ...formData };
        if (!accessConfig.invite_code_required) {
          delete registerData.invite_code;
        }
        await api.register(registerData);
        // Auto login after registration
        const loginData = { email: formData.email, password: formData.password };
        if (accessConfig.access_protection_enabled) {
          loginData.access_code = formData.access_code;
        }
        const loginRes = await api.login(loginData);
        onLogin(loginRes.data.user, loginRes.data.token);
      } else {
        const loginData = { email: formData.email, password: formData.password };
        if (accessConfig.access_protection_enabled) {
          loginData.access_code = formData.access_code;
        }
        const res = await api.login(loginData);
        onLogin(res.data.user, res.data.token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'caregiver', labelKey: 'auth.caregiver', icon: Heart, color: 'bg-[#10B981]' },
    { value: 'frontline_worker', labelKey: 'auth.frontlineWorker', icon: Users, color: 'bg-[#3B82F6]' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'हिन्दी (Hindi)' },
    { value: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
    { value: 'ta', label: 'தமிழ் (Tamil)' },
    { value: 'te', label: 'తెలుగు (Telugu)' },
    { value: 'ml', label: 'മലയാളം (Malayalam)' },
    { value: 'mr', label: 'मराठी (Marathi)' },
    { value: 'ur', label: 'اردو (Urdu)' }
  ];

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      <div className="flex flex-1">
        {/* Left Side - Hero */}
        <div 
          className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0F4C81] to-[#1e5a9a] p-12 flex-col justify-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1627138756965-467f304eaacd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBoZWFsdGglMjB3b3JrZXIlMjBjaGlsZHxlbnwwfHx8fDE3NzE0MTc0NTd8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border-2 border-white/20">
            {/* HCS Logo in Hero */}
            <div className="flex items-center mb-6">
              <img 
                src={HCS_LOGO_URL} 
                alt="HCS Logo" 
                className="h-16 w-auto object-contain bg-white rounded-lg p-2"
              />
              <span className="ml-4 text-2xl font-bold text-white font-['Nunito']">ECD-AI Connect</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Nunito']">
              {t('auth.heroTitle')}
            </h1>
            <p className="text-lg text-white/90 leading-relaxed mb-8">
              {t('auth.heroDescription')}
            </p>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#10B981] rounded-full flex items-center justify-center">
                  <Heart className="text-white" size={24} />
                </div>
                <span className="text-white font-semibold text-lg">{t('auth.feature1')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#F59E0B] rounded-full flex items-center justify-center">
                  <Users className="text-black" size={24} />
                </div>
                <span className="text-white font-semibold text-lg">{t('auth.feature2')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#3B82F6] rounded-full flex items-center justify-center">
                  <Shield className="text-white" size={24} />
                </div>
                <span className="text-white font-semibold text-lg">{t('auth.feature3')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#FAFAF9]">
          <div className="w-full max-w-md">
            {/* Language Selector at top right */}
            <div className="flex justify-end mb-4">
              <LanguageSelector />
            </div>
            
            {/* Logo for mobile */}
            <div className="lg:hidden flex items-center justify-center mb-8">
              <img 
                src={HCS_LOGO_URL} 
                alt="HCS Logo" 
                className="h-16 w-auto object-contain"
              />
              <span className="ml-3 text-2xl font-bold text-[#0F4C81] font-['Nunito']">ECD-AI Connect</span>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="form-title">
                {isRegister ? t('auth.register') : t('auth.welcomeBack')}
              </h2>
              <p className="text-[#4B5563] mb-6">
                {isRegister ? t('auth.joinPlatform') : t('auth.signInAccount')}
              </p>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" data-testid="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {isRegister && (
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.name')}</label>
                    <input
                      type="text"
                      data-testid="name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.email')}</label>
                  <input
                    type="email"
                    data-testid="email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.password')}</label>
                  <input
                    type="password"
                    data-testid="password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                    required
                  />
                </div>

                {/* Access Code Field - Only shown when protection is enabled */}
                {accessConfig.access_protection_enabled && !isRegister && (
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">
                      <Lock className="inline-block mr-1" size={14} />
                      Access Code
                    </label>
                    <input
                      type="text"
                      data-testid="access-code-input"
                      value={formData.access_code}
                      onChange={(e) => setFormData({ ...formData, access_code: e.target.value })}
                      placeholder="Enter your access code"
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-amber-300 bg-amber-50 focus:border-[#F59E0B] focus:ring-4 focus:ring-amber-100 transition-all outline-none"
                      required
                    />
                    <p className="text-xs text-amber-600 mt-1">Contact your administrator for access code</p>
                  </div>
                )}

                {/* Invite Code Field - Only shown for registration when required */}
                {accessConfig.invite_code_required && isRegister && (
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">
                      <Shield className="inline-block mr-1" size={14} />
                      Invite Code
                    </label>
                    <input
                      type="text"
                      data-testid="invite-code-input"
                      value={formData.invite_code}
                      onChange={(e) => setFormData({ ...formData, invite_code: e.target.value })}
                      placeholder="Enter your invite code"
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-purple-300 bg-purple-50 focus:border-[#8B5CF6] focus:ring-4 focus:ring-purple-100 transition-all outline-none"
                      required
                    />
                    <p className="text-xs text-purple-600 mt-1">Invite code is required to register</p>
                  </div>
                )}

                {isRegister && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.role')}</label>
                      <div className="space-y-2">
                        {roleOptions.map((option) => {
                          const Icon = option.icon;
                          return (
                            <label
                              key={option.value}
                              className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all relative z-10 ${
                                formData.role === option.value
                                  ? 'border-[#0F4C81] bg-blue-50'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              onClick={() => setFormData({ ...formData, role: option.value })}
                            >
                              <input
                                type="radio"
                                data-testid={`role-${option.value}`}
                                name="role"
                                value={option.value}
                                checked={formData.role === option.value}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                className="sr-only"
                              />
                              <div className={`w-10 h-10 ${option.color} rounded-full flex items-center justify-center mr-3 pointer-events-none`}>
                                <Icon className="text-white" size={20} />
                              </div>
                              <span className="font-semibold text-[#111827] pointer-events-none">{t(option.labelKey)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.language')}</label>
                      <select
                        data-testid="language-select"
                        value={formData.language}
                        onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                        className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      >
                        {languageOptions.map((lang) => (
                          <option key={lang.value} value={lang.value}>{lang.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.phone')}</label>
                      <input
                        type="tel"
                        data-testid="phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      />
                    </div>

                    {/* State Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.selectState')}</label>
                      <select
                        data-testid="state-select"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      >
                        <option value="">{t('auth.selectStatePlaceholder')}</option>
                        {states.map((state) => (
                          <option key={state.code} value={state.code}>{state.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* District Input - for Frontline Workers */}
                    {formData.role === 'frontline_worker' && (
                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">{t('auth.district')}</label>
                        <input
                          type="text"
                          data-testid="district-input"
                          value={formData.district}
                          onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                          placeholder={t('auth.enterDistrict')}
                          className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                        />
                      </div>
                    )}
                  </>
                )}

                <button
                  type="submit"
                  data-testid="submit-button"
                  disabled={loading}
                  className="w-full rounded-full px-8 py-4 text-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95 bg-[#0F4C81] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('common.pleaseWait') : isRegister ? t('auth.register') : t('auth.login')}
                </button>
              </form>

              {!isRegister && (
                <div className="mt-4 text-center">
                  <a
                    href="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-[#3B82F6] font-semibold hover:underline text-sm"
                  >
                    {t('auth.forgotPassword')}
                  </a>
                </div>
              )}

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setIsRegister(!isRegister);
                    setError('');
                  }}
                  data-testid="toggle-form-button"
                  className="text-[#0F4C81] font-semibold hover:underline"
                >
                  {isRegister ? t('auth.alreadyHaveAccount') : t('auth.noAccount')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default Login;
